<script lang="ts" setup>
import { onBeforeUnmount, ref, watch } from "vue";
import { Popup, showLoadingToast } from "vant";
import { useRouter } from "vue-router";
import { useChatSession } from "@/stores/chatSession";
import i18n from "@/locales";
import { createPrivateSession as createPrivateChat } from "@/components/mobile/chat/chatUtils";

const show = ref(false);
const router = useRouter();
const waitingTips: string[] = [
  i18n.global.t("Chats.waitingTips.0"),
  i18n.global.t("Chats.waitingTips.1"),
  i18n.global.t("Chats.waitingTips.2"),
  i18n.global.t("Chats.waitingTips.3"),
  i18n.global.t("Chats.waitingTips.4"),
  i18n.global.t("Chats.waitingTips.5"),
  i18n.global.t("Chats.waitingTips.6"),
  i18n.global.t("Chats.waitingTips.7"),
];
const currentTip = ref(waitingTips[0]);
let i = 0;
let interval: number | null = null;
const chatSessionStore = useChatSession();
watch(show, (show) => {
  if (show) {
    interval = window.setInterval(() => {
      const idx = Math.min(i, waitingTips.length - 1);
      currentTip.value = waitingTips[idx];
      i++;
    }, 1000);
  } else {
    if (interval) window.clearInterval(interval);
    interval = null;
    i = 0;
  }
});

onBeforeUnmount(() => {
  if (interval) window.clearInterval(interval);
});

const createPrivateSession = async (
  mate: Mate,
  routerEnterMode: "push" | "replace" | "none" = "push"
) => {
  if (!mate) return;
  await createPrivateChat(mate.id!, mate.uuid, routerEnterMode);
};

const enterSceneMode = async (sessionId: number, mate: Mate) => {
  show.value = true;
  await chatSessionStore.updateSessionMode(sessionId, mate, 1);
  show.value = false;
};

export interface WaitingSessionCreationInstance {
  createPrivateSession: typeof createPrivateSession;
  enterSceneMode: typeof enterSceneMode;
}

defineExpose({
  createPrivateSession,
  enterSceneMode,
});
</script>
<template>
  <popup
    v-model:show="show"
    :closeable="false"
    class="h-full"
    position="bottom"
    teleport="body"
  >
    <div class="relative w-full h-full flex items-center justify-center">
      <div class="absolute w-full h-full bg-container"></div>
      <div class="flex flex-col items-center justify-center">
        <div
          class="text-xl md:text-2xl font-bold text-center text-base-content/50"
        >
          {{ currentTip }}...
        </div>
        <div class="mt-4">
          <span class="loading loading-bars"></span>
        </div>
      </div>
    </div>
  </popup>
</template>
<style scoped>
@keyframes backgroundScroll {
  from {
    background-position: 50% 10%;
  }
  to {
    background-position: 0 0;
  }
}

.bg-container {
  background-repeat: repeat;
  animation: backgroundScroll 10s linear infinite;
  opacity: 0.2;
  background-size: 50%;
  filter: grayscale(100%);
}
</style>
